import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, ErrorNote, Eyebrow } from '../../components/ui'
import { api } from '../../lib/api'
import { fmtDate } from '../../lib/format'
import { useT } from '../../lib/i18n'

interface Grant {
  id: string
  clientId: string
  scope: string[]
  metadata?: { client?: string | null; target?: string | null }
  createdAt: number
}

/** MCP apps the signed-in person connected through OAuth (plan, 11.2: revoke access in settings). */
export function ConnectedApps() {
  const { t, locale } = useT()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['oauth-grants'],
    queryFn: () => api<{ grants: Grant[] }>('/api/oauth/grants'),
    retry: false,
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/oauth/grants/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['oauth-grants'] }),
  })
  if (q.error) return null // OAuth is not configured in this environment
  const grants = q.data?.grants ?? []
  return (
    <Card className="mt-4">
      <Eyebrow className="mb-2">{t('connectedApps')}</Eyebrow>
      <p className="mb-4 text-sm text-body">{t('connectedAppsHint')}</p>
      {!grants.length ? (
        <p className="text-sm text-mute">{t('noConnectedApps')}</p>
      ) : (
        <div className="grid gap-2">
          {grants.map((g) => (
            <div
              key={g.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-hairline px-4 py-3"
            >
              <div>
                <div className="text-ink">{g.metadata?.client ?? g.clientId}</div>
                <div className="mono text-xs text-mute">
                  {g.scope.join(' · ')} · {fmtDate(new Date(g.createdAt * 1000).toISOString(), locale)}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-danger ml-auto"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(g.id)}
              >
                {t('revoke')}
              </button>
            </div>
          ))}
        </div>
      )}
      <ErrorNote error={revoke.error} />
    </Card>
  )
}
